import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useChangePassword } from "../api";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const changePasswordMutation = useChangePassword();

  const [passwordData, setPasswordData] = useState({
    password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordError, setPasswordError] = useState({
    password: "",
    new_password: "",
    confirm_password: "",
  });

  if (!open) return null;

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordError({ password: "", new_password: "", confirm_password: "" });
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const closePasswordModal = () => {
    setPasswordData({ password: "", new_password: "", confirm_password: "" });
    setPasswordError({ password: "", new_password: "", confirm_password: "" });
    onClose();
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const passwordRegx = new RegExp(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/
    );

    let hasError = false;
    const errors = { password: "", new_password: "", confirm_password: "" };

    if (!passwordData.password) {
      errors.password = "Current password is required";
      hasError = true;
    }
    if (!passwordData.new_password) {
      errors.new_password = "New password is required";
      hasError = true;
    } else if (!passwordRegx.test(passwordData.new_password)) {
      errors.new_password =
        "Password must contain at least 1 uppercase, 1 lowercase, 1 digit, 1 symbol, and be at least 8 characters.";
      hasError = true;
    }
    if (!passwordData.confirm_password) {
      errors.confirm_password = "Confirm password is required";
      hasError = true;
    } else if (passwordData.confirm_password !== passwordData.new_password) {
      errors.confirm_password = "Passwords do not match";
      hasError = true;
    }

    if (hasError) {
      setPasswordError(errors);
      return;
    }

    changePasswordMutation.mutate(passwordData, {
      onSuccess: () => {
        toast.success("Password changed successfully");
        closePasswordModal();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || "Failed to change password");
      },
    });
  };

  return (
    <div className="v2 fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
      <div className="bg-card text-card-foreground rounded-xl max-w-sm w-full shadow-xl border border-border flex flex-col text-left" style={{ padding: "24px" }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-foreground">Change Password</h3>
          <button
            onClick={closePasswordModal}
            className="text-muted-foreground hover:text-foreground rounded-full p-1 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-muted-foreground" style={{ color: "#334155" }}>Current Password</label>
            <input
              type="password"
              name="password"
              value={passwordData.password}
              onChange={handlePasswordChange}
              className={`w-full px-2 h-10 border ${
                passwordError.password ? "border-destructive" : "border-border"
              } bg-card rounded-md text-sm focus:outline-none focus:border-action font-medium text-foreground`}
            />
            {passwordError.password && (
              <span className="text-[10px] text-destructive mt-1 block font-medium">{passwordError.password}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-muted-foreground" style={{ color: "#334155" }}>New Password</label>
            <input
              type="password"
              name="new_password"
              value={passwordData.new_password}
              onChange={handlePasswordChange}
              className={`w-full px-2 h-10 border ${
                passwordError.new_password ? "border-destructive" : "border-border"
              } bg-card rounded-md text-sm focus:outline-none focus:border-action font-medium text-foreground`}
            />
            {passwordError.new_password && (
              <span className="text-[10px] text-destructive mt-1 block font-medium">{passwordError.new_password}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-muted-foreground" style={{ color: "#334155" }}>Confirm New Password</label>
            <input
              type="password"
              name="confirm_password"
              value={passwordData.confirm_password}
              onChange={handlePasswordChange}
              className={`w-full px-2 h-10 border ${
                passwordError.confirm_password ? "border-destructive" : "border-border"
              } bg-card rounded-md text-sm focus:outline-none focus:border-action font-medium text-foreground`}
            />
            {passwordError.confirm_password && (
              <span className="text-[10px] text-destructive mt-1 block font-medium">{passwordError.confirm_password}</span>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={closePasswordModal}
              disabled={changePasswordMutation.isPending}
              className="flex-1 h-11 border border-slate-200 text-slate-700 font-semibold text-sm rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={changePasswordMutation.isPending}
              className="flex-1 h-11 text-white font-bold text-sm rounded-lg hover:opacity-90 transition-colors flex items-center justify-center gap-2"
              style={{ backgroundColor: "var(--v2-action)" }}
            >
              {changePasswordMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
